/**
 * MaestroRecursos
 * Página para gestionar el maestro de Recursos
 * CRUD completo para crear, editar, listar y eliminar recursos
 * Los recursos se muestran de forma independiente sin agrupar por partida o área
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiArrowLeft, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import presupuestoHierarchyService from '../../services/presupuestoHierarchyService';
import CrearRecursoModal from '../../components/maestros/CrearRecursoModal';
import EditarRecursoModal from '../../components/maestros/EditarRecursoModal';
import Loading from '../../components/Loading';
import '../../styles/maestros.css';

const MaestroRecursos = () => {
  const navigate = useNavigate();
  const [recursos, setRecursos] = useState([]);
  const [filteredRecursos, setFilteredRecursos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [crearModalOpen, setCrearModalOpen] = useState(false);
  const [editarModalOpen, setEditarModalOpen] = useState(false);
  const [recursoSeleccionado, setRecursoSeleccionado] = useState(null);

  useEffect(() => {
    cargarRecursos();
  }, []);

  useEffect(() => {
    // Filtrar recursos según búsqueda
    if (searchTerm.trim() === '') {
      setFilteredRecursos(recursos);
    } else {
      const filtered = recursos.filter(recurso =>
        recurso.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (recurso.descripcion && recurso.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredRecursos(filtered);
    }
  }, [searchTerm, recursos]);

  const cargarRecursos = async () => {
    setLoading(true);
    try {
      const response = await presupuestoHierarchyService.getMaestroRecursos();
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setRecursos(data);
      setFilteredRecursos(data);
    } catch (error) {
      console.error('Error al cargar recursos:', error);
      toast.error('Error al cargar los recursos');
    } finally {
      setLoading(false);
    }
  };


  const handleEliminarRecurso = async (recursoId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este recurso?')) {
      return;
    }

    try {
      await presupuestoHierarchyService.deleteMaestroRecurso(recursoId);
      toast.success('Recurso eliminado exitosamente');
      cargarRecursos();
    } catch (error) {
      console.error('Error al eliminar recurso:', error);
      toast.error('Error al eliminar el recurso');
    }
  };

  const handleCrearRecurso = async (datosRecurso) => {
    try {
      await presupuestoHierarchyService.createMaestroRecurso(datosRecurso);
      toast.success('Recurso creado exitosamente');
      setCrearModalOpen(false);
      cargarRecursos();
    } catch (error) {
      console.error('Error al crear recurso:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el recurso');
    }
  };

  const handleEditarRecurso = async (datosRecurso) => {
    try {
      await presupuestoHierarchyService.updateMaestroRecurso(recursoSeleccionado.id, datosRecurso);
      toast.success('Recurso actualizado exitosamente');
      setEditarModalOpen(false);
      setRecursoSeleccionado(null);
      cargarRecursos();
    } catch (error) {
      console.error('Error al actualizar recurso:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar el recurso');
    }
  };


  if (loading) {
    return <Loading />;
  }

  return (
    <div className="maestro-recursos">
      <div className="header">
        <div>
          <h1>Maestro de Recursos</h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.95rem' }}>
            Total de recursos: <strong>{recursos.length}</strong>
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setCrearModalOpen(true)}
        >
          <FiPlus /> Nuevo Recurso
        </button>
      </div>

      {/* Buscador */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FiSearch style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            type="text"
            placeholder="Buscar recursos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: '2.5rem',
              paddingRight: '1rem',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.95rem',
              fontFamily: 'inherit'
            }}
          />
        </div>
      </div>

      {recursos.length === 0 ? (
        <div className="empty-state">
          <p>No hay recursos creados aún</p>
          <button
            className="btn btn-primary"
            onClick={() => setCrearModalOpen(true)}
          >
            Crear Primer Recurso
          </button>
        </div>
      ) : filteredRecursos.length === 0 ? (
        <div className="empty-state">
          <p>No hay recursos que coincidan con la búsqueda</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{
                background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                color: 'white'
              }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Nombre</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Descripción</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Unidad</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Tipo</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Clase Recurso</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Stock</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Estado</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecursos.map((recurso, index) => (
                <tr key={recurso.id} style={{
                  borderBottom: '1px solid #e5e7eb',
                  transition: 'background-color 0.2s',
                  backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9fafb'}
                >
                  <td style={{ padding: '1rem', fontWeight: '500' }}>{recurso.nombre}</td>
                  <td style={{ padding: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                    {recurso.descripcion || '-'}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}>
                      {recurso.unidad}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {recurso.tipo_recurso_data?.nombre || '-'}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {recurso.clase_recurso_data ? (
                      <span title={recurso.clase_recurso_data.nombre}>
                        {recurso.clase_recurso_data.codigo_completo || recurso.clase_recurso_data.codigo} - {recurso.clase_recurso_data.nombre}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    {recurso.stock ? (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        backgroundColor: '#dcfce7',
                        color: '#15803d',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>
                        Sí
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: recurso.status === 'activo' ? '#dcfce7' : '#fee2e2',
                      color: recurso.status === 'activo' ? '#15803d' : '#991b1b',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {recurso.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setRecursoSeleccionado(recurso);
                          setEditarModalOpen(true);
                        }}
                        title="Editar recurso"
                        style={{
                          padding: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleEliminarRecurso(recurso.id)}
                        title="Eliminar recurso"
                        style={{
                          padding: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CrearRecursoModal
        isOpen={crearModalOpen}
        onClose={() => setCrearModalOpen(false)}
        onSubmit={handleCrearRecurso}
      />

      {recursoSeleccionado && (
        <EditarRecursoModal
          isOpen={editarModalOpen}
          onClose={() => {
            setEditarModalOpen(false);
            setRecursoSeleccionado(null);
          }}
          recurso={recursoSeleccionado}
          onSubmit={handleEditarRecurso}
        />
      )}
    </div>
  );
};

export default MaestroRecursos;
